import React, { useState } from "react";
import { toast } from "sonner";
import {
  Copy, ExternalLink, ShieldAlert, ShieldCheck, Target, Megaphone,
  Type, MousePointerClick, Image, AlertTriangle, CheckCircle2, ArrowRight,
  Settings, Search, PoundSterling, MapPin, Zap, ChevronDown, ChevronUp,
} from "lucide-react";

/**
 * Launch Ads — single-page playbook for setting up Peptigen Labs paid Search.
 *
 * Goal: zero ambiguity. Every keyword, ad copy line, sitelink and callout has a
 * one-click Copy button. Plus a do-NOT-do list so reviewers don't reject the
 * account, and a step-by-step Google Ads UI walkthrough.
 */

const KEYWORDS = [
  "peptigen labs",
  "peptigen labs uk",
  "research peptides uk",
  "uk research peptide supplier",
  "british research peptides",
  "laboratory peptides uk",
  "research peptide supplier",
  "lyophilised peptides uk",
  "purity tested peptides",
  "research compounds uk supplier",
  "laboratory reagents uk peptide",
  "certificate of analysis peptides",
  "peptide research supplier",
  "research grade peptides",
  "uk lab peptide supplier",
];

const NEGATIVE_KEYWORDS = [
  "buy", "cheap", "cheapest", "for sale", "weight loss", "fat loss",
  "muscle gain", "muscle building", "anti aging", "anti ageing",
  "performance", "bodybuilding", "tan", "tanning", "libido", "ozempic",
  "wegovy", "mounjaro", "where to buy", "how to inject", "dose", "dosage",
];

const ADS = [
  {
    angle: "Supplier",
    headlines: [
      "UK Research Peptide Supplier",
      "Same-Day UK Dispatch",
      "Independently Purity Tested",
    ],
    descriptions: [
      "British supplier of research-use-only peptides. Batch documented. Certificate of Analysis with every order.",
      "Trusted by UK laboratories. Order before 14:00 for next-day delivery.",
    ],
    finalUrl: "https://peptigenlabs.co.uk/lp/research-supplier-uk",
  },
  {
    angle: "Quality",
    headlines: [
      "Lyophilised Research Peptides",
      "≥99% Purity Tested",
      "UK Stock · Tracked Delivery",
    ],
    descriptions: [
      "Reference-grade research peptides for UK laboratories. Reversed-phase HPLC tested. Batch traceability included.",
      "For qualified scientific buyers and laboratory researchers. Research use only.",
    ],
    finalUrl: "https://peptigenlabs.co.uk/lp/lab-peptides-uk",
  },
  {
    angle: "Speed",
    headlines: [
      "Research Peptides UK",
      "Order Before 14:00 GMT",
      "Next Working Day Delivery",
    ],
    descriptions: [
      "British research peptide supplier. UK-held stock, same-day dispatch, full batch documentation.",
      "Independently tested. Research-use-only. For qualified scientific buyers.",
    ],
    finalUrl: "https://peptigenlabs.co.uk/lp/research-supplier-uk",
  },
];

const SITELINKS = [
  { name: "Quality & Testing", url: "https://peptigenlabs.co.uk/quality" },
  { name: "Same-Day Dispatch", url: "https://peptigenlabs.co.uk/same-day-dispatch" },
  { name: "Currently Available", url: "https://peptigenlabs.co.uk/currently-available" },
  { name: "About Peptigen Labs", url: "https://peptigenlabs.co.uk/about" },
];

const CALLOUTS = [
  "UK based supplier",
  "Independently purity tested",
  "Same-day UK dispatch",
  "Tracked next-day delivery",
  "Batch documented",
  "Certificate of Analysis",
  "Research use only",
  "Qualified scientific buyers",
];

const STRUCTURED_SNIPPET = {
  header: "Service catalog",
  values: ["Lyophilised peptides", "Purity testing", "Batch documentation", "Same-day UK dispatch"],
};

// ───────────────────────────────────────────────────────────────────────────────

function CopyBtn({ text, label, testid, full = false }) {
  const [copied, setCopied] = useState(false);
  const onClick = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(label ? `${label} copied` : "Copied");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Could not copy — please copy manually");
    }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={"inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-[10px] font-extrabold tracking-[0.16em] uppercase " + (full ? "w-full justify-center" : "")}
      style={{
        background: copied ? "#A6FF00" : "#0F172A",
        color: copied ? "#0B1220" : "#5EEAD4",
        border: "1px solid " + (copied ? "#A6FF00" : "#1E2E44"),
        cursor: "pointer",
      }}
    >
      {copied ? <CheckCircle2 size={11} /> : <Copy size={11} />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

function Section({ icon: Icon, title, kicker, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="pg-card overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full p-5 flex items-center justify-between gap-4 text-left"
      >
        <div className="flex items-center gap-3">
          <Icon size={18} className="text-[#A6FF00]" />
          <div>
            <div className="pg-micro mb-0.5">{kicker}</div>
            <div className="text-[#F7FAFC] pg-heading text-lg" style={{ fontFamily: "'Rajdhani'" }}>
              {title}
            </div>
          </div>
        </div>
        {open ? <ChevronUp size={18} className="text-[#9CA8B8]" /> : <ChevronDown size={18} className="text-[#9CA8B8]" />}
      </button>
      {open && <div className="px-5 pb-5 pt-1 border-t border-[#1E2E44]">{children}</div>}
    </div>
  );
}

function Step({ n, title, body, action }) {
  return (
    <div className="grid grid-cols-[40px_1fr] gap-4 mb-4">
      <div
        className="w-9 h-9 rounded-sm flex items-center justify-center text-[#0B1220] font-extrabold text-base"
        style={{ background: "#5EEAD4", fontFamily: "'Rajdhani'" }}
      >
        {n}
      </div>
      <div>
        <div className="text-[#F7FAFC] font-bold text-[15px] mb-0.5">{title}</div>
        {body && <div className="text-[#9CA8B8] text-[13px] leading-relaxed">{body}</div>}
        {action && <div className="mt-2">{action}</div>}
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────────

export default function AdminAdsLaunch() {
  return (
    <div data-testid="admin-ads-launch-page" className="space-y-6">
      {/* Header */}
      <div>
        <div className="pg-micro mb-2">// Admin · Launch your first Google Ads campaign</div>
        <h1 className="text-[#F7FAFC] text-3xl pg-display" style={{ fontFamily: "'Exo 2'" }}>
          Launch Ads — copy & paste playbook
        </h1>
        <p className="text-[#9CA8B8] text-sm mt-2 max-w-3xl leading-relaxed">
          Open <a href="https://ads.google.com" target="_blank" rel="noreferrer" className="text-[#5EEAD4] hover:underline">ads.google.com</a> in another tab.
          Click "+ Create campaign". Follow the steps below — every keyword, headline and
          callout has a one-click copy button. Total time: ~15 minutes.
        </p>
      </div>

      {/* Quick-launch */}
      <div className="pg-card p-5 flex flex-wrap items-center justify-between gap-3 border-2 border-[#A6FF00]/40">
        <div className="flex items-center gap-3">
          <Zap size={20} className="text-[#A6FF00]" />
          <div>
            <div className="text-[#F7FAFC] text-base font-bold">Ready to start?</div>
            <div className="text-[#9CA8B8] text-[12px]">Opens Google Ads in a new tab — straight to campaign creation.</div>
          </div>
        </div>
        <a
          href="https://ads.google.com/aw/campaigns/new"
          target="_blank"
          rel="noreferrer"
          data-testid="ads-launch-open-google-ads"
          className="pg-btn-primary"
        >
          Open Google Ads <ExternalLink size={14} />
        </a>
      </div>

      {/* THE RED PANEL — things never to do */}
      <div className="pg-card p-5 border-2" style={{ borderColor: "rgba(239,68,68,0.5)", background: "rgba(239,68,68,0.06)" }}>
        <div className="flex items-center gap-2 mb-3">
          <ShieldAlert size={18} className="text-[#EF4444]" />
          <div className="text-[#F7FAFC] pg-heading text-lg" style={{ fontFamily: "'Rajdhani'" }}>
            ⚠️ Never do these — instant ad rejection or account ban
          </div>
        </div>
        <ul className="space-y-2 text-[13px] text-[#F7FAFC]">
          {[
            "Pick \"Performance Max\" campaign type — peptide brands get banned within 48 h on PMax. Always pick \"Search\".",
            "Tick \"Display Network\" or \"Search partners\" in network settings — Display reviewers are 10× stricter on healthcare.",
            "Use the words: buy, cheap, weight loss, fat loss, anti-aging, muscle gain, performance, bodybuilding, libido — anywhere in headlines, descriptions, sitelinks, callouts or extensions.",
            "Bid on named-molecule keywords (retatrutide, BPC-157, GHK-Cu, semaglutide, tirzepatide, Ozempic, Wegovy, Mounjaro). Reviewers auto-reject these.",
            "Upload photos of vials, pills or syringes as image assets. Use only your logo or the lab/scientist hero artwork.",
            "Turn on \"Auto-applied recommendations\". Google will silently expand keywords to broad-match — instant rejection.",
            "Appeal a rejected ad more than once. Two rejections on the same ad = account-level flag. Just delete and rewrite.",
          ].map((line, i) => (
            <li key={i} className="flex items-start gap-2">
              <AlertTriangle size={13} className="text-[#EF4444] shrink-0 mt-0.5" />
              <span>{line}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* STEP 1 — Goal & Type */}
      <Section icon={Target} kicker="// Step 1" title="Campaign goal & type" defaultOpen={true}>
        <Step n="1" title="Click '+ Create campaign'" body="Top-left corner of Google Ads, the blue '+ Create' button → 'Campaign'." />
        <Step n="2" title="Goal: Sales" body="Pick 'Sales'. Tick ONLY the 'Purchase' conversion goal (already wired up by us). Untick everything else." />
        <Step n="3" title="Campaign type: Search" body="Pick 'Search' — NOT Performance Max, NOT Display, NOT Shopping. Search is the only campaign type that survives peptide policy review." />
        <Step n="4" title="Conversion goals" body="If asked which conversion to optimise for, pick 'Purchase'." />
      </Section>

      {/* STEP 2 — Bidding & Budget */}
      <Section icon={PoundSterling} kicker="// Step 2" title="Bidding & budget">
        <Step
          n="1"
          title="Bidding strategy: Maximize conversions"
          body="Pick 'Maximize conversions'. Do NOT set a target CPA yet — Google needs ~30 conversions before that works. Leave the target CPA box empty."
        />
        <Step
          n="2"
          title="Daily budget: £20"
          body="That's the hard ceiling per day. Google may spend up to 2× on big-traffic days but averages out to £20/day across the month. Adjust later once you see what converts."
        />
      </Section>

      {/* STEP 3 — Networks & Locations */}
      <Section icon={MapPin} kicker="// Step 3" title="Networks, locations, language">
        <Step
          n="1"
          title="UNTICK Display Network"
          body="Critical. The Display tickbox is on by default. Untick it. This single setting is responsible for ~70% of peptide-brand account bans."
        />
        <Step n="2" title="UNTICK Search partners" body="Lower-quality search engines, extra rejection risk. Untick." />
        <Step n="3" title="Locations: United Kingdom" body="Type 'United Kingdom' and select it. Remove any other countries." />
        <Step n="4" title="Languages: English" body="Default is fine. Don't add others." />
        <Step n="5" title="Audience segments: leave blank" body="We'll add remarketing audiences in 30 days once you have enough website visitors." />
      </Section>

      {/* STEP 4 — Keywords */}
      <Section icon={Search} kicker="// Step 4" title="Keywords (the most important step)">
        <p className="text-[#9CA8B8] text-[13px] leading-relaxed mb-4">
          Click "Get keyword ideas" or "Add keywords manually". Paste the list below into the keyword box.
          IMPORTANT: change the match type from <span className="text-[#F7FAFC] font-semibold">Broad</span> to{" "}
          <span className="text-[#A6FF00] font-semibold">Phrase match</span> — wrap each keyword in &quot;double quotes&quot;.
          The copy button below already adds the quotes for you.
        </p>
        <div className="bg-[#0B1220] border border-[#1E2E44] rounded-sm p-4 mb-3">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="pg-micro">// 15 brand-safe phrase-match keywords</div>
            <CopyBtn
              text={KEYWORDS.map((k) => `"${k}"`).join("\n")}
              label="All keywords"
              testid="ads-launch-copy-all-keywords"
            />
          </div>
          <pre className="text-[12px] text-[#5EEAD4] pg-mono whitespace-pre-wrap leading-relaxed m-0">
{KEYWORDS.map((k) => `"${k}"`).join("\n")}
          </pre>
        </div>

        <div className="bg-[#0B1220] border border-[#1E2E44] rounded-sm p-4">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="pg-micro" style={{ color: "#EF4444" }}>// Negative keywords — paste these too</div>
            <CopyBtn
              text={NEGATIVE_KEYWORDS.join("\n")}
              label="Negative keywords"
              testid="ads-launch-copy-negative-keywords"
            />
          </div>
          <p className="text-[#9CA8B8] text-[11.5px] mb-2 leading-relaxed">
            In Campaign settings → Keywords → "Negative keywords", paste these. They block your ads from showing on dangerous queries (the words that get peptide brands flagged).
          </p>
          <pre className="text-[11px] text-[#9CA8B8] pg-mono whitespace-pre-wrap leading-relaxed m-0">
{NEGATIVE_KEYWORDS.join(", ")}
          </pre>
        </div>
      </Section>

      {/* STEP 5 — Ads */}
      <Section icon={Megaphone} kicker="// Step 5" title="Write 3 ads (copy them all in)">
        <p className="text-[#9CA8B8] text-[13px] leading-relaxed mb-4">
          For each ad: in Google Ads, click "+ New ad" → pick "Responsive search ad" → paste the headlines and descriptions one by one,
          then paste the Final URL at the bottom. Repeat for all 3 ads.
        </p>
        {ADS.map((ad, idx) => (
          <div key={idx} className="bg-[#0B1220] border border-[#1E2E44] rounded-sm p-4 mb-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[#F7FAFC] font-bold text-[14px]">
                Ad {idx + 1} — <span className="text-[#5EEAD4]">{ad.angle} angle</span>
              </div>
            </div>

            <div className="mb-3">
              <div className="pg-micro mb-2">// Headlines (paste each into a separate Headline slot)</div>
              <div className="space-y-1.5">
                {ad.headlines.map((h, hi) => (
                  <div key={hi} className="flex items-center justify-between gap-2 bg-[#0F172A] px-3 py-2 rounded-sm">
                    <code className="text-[#F7FAFC] text-[12.5px] flex-1">{h}</code>
                    <span className="text-[10px] text-[#9CA8B8] pg-mono">{h.length}/30</span>
                    <CopyBtn text={h} label="Headline" testid={`ads-launch-copy-headline-${idx}-${hi}`} />
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-3">
              <div className="pg-micro mb-2">// Descriptions (paste each into a separate Description slot)</div>
              <div className="space-y-1.5">
                {ad.descriptions.map((d, di) => (
                  <div key={di} className="flex items-start justify-between gap-2 bg-[#0F172A] px-3 py-2 rounded-sm">
                    <code className="text-[#F7FAFC] text-[12.5px] flex-1 leading-snug">{d}</code>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[10px] text-[#9CA8B8] pg-mono">{d.length}/90</span>
                      <CopyBtn text={d} label="Description" testid={`ads-launch-copy-desc-${idx}-${di}`} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="pg-micro mb-2">// Final URL (the page Google sends people to)</div>
              <div className="flex items-center justify-between gap-2 bg-[#0F172A] px-3 py-2 rounded-sm">
                <code className="text-[#5EEAD4] text-[12px] flex-1 break-all">{ad.finalUrl}</code>
                <a href={ad.finalUrl} target="_blank" rel="noreferrer" className="text-[#9CA8B8] hover:text-[#F7FAFC]">
                  <ExternalLink size={12} />
                </a>
                <CopyBtn text={ad.finalUrl} label="URL" testid={`ads-launch-copy-url-${idx}`} />
              </div>
            </div>
          </div>
        ))}
      </Section>

      {/* STEP 6 — Extensions */}
      <Section icon={MousePointerClick} kicker="// Step 6" title="Ad extensions (free clicks)">
        <p className="text-[#9CA8B8] text-[13px] leading-relaxed mb-4">
          In Google Ads campaign view → "Assets" tab → "+ Add asset" → pick the type. Add all four below.
        </p>

        {/* Sitelinks */}
        <div className="bg-[#0B1220] border border-[#1E2E44] rounded-sm p-4 mb-4">
          <div className="text-[#F7FAFC] font-bold text-[13px] mb-2">Sitelinks (4)</div>
          <div className="space-y-1.5">
            {SITELINKS.map((s, i) => (
              <div key={i} className="flex items-center justify-between gap-2 bg-[#0F172A] px-3 py-2 rounded-sm">
                <div className="flex-1 min-w-0">
                  <div className="text-[#F7FAFC] text-[12.5px]">{s.name}</div>
                  <div className="text-[#5EEAD4] text-[11px] pg-mono truncate">{s.url}</div>
                </div>
                <CopyBtn text={s.name} label="Name" testid={`ads-launch-copy-sitelink-name-${i}`} />
                <CopyBtn text={s.url} label="URL" testid={`ads-launch-copy-sitelink-url-${i}`} />
              </div>
            ))}
          </div>
        </div>

        {/* Callouts */}
        <div className="bg-[#0B1220] border border-[#1E2E44] rounded-sm p-4 mb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[#F7FAFC] font-bold text-[13px]">Callouts (8)</div>
            <CopyBtn text={CALLOUTS.join("\n")} label="All callouts" testid="ads-launch-copy-all-callouts" />
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            {CALLOUTS.map((c, i) => (
              <div key={i} className="bg-[#0F172A] px-3 py-1.5 rounded-sm flex items-center justify-between gap-2">
                <code className="text-[#F7FAFC] text-[12px]">{c}</code>
                <CopyBtn text={c} label="Callout" testid={`ads-launch-copy-callout-${i}`} />
              </div>
            ))}
          </div>
        </div>

        {/* Structured snippet */}
        <div className="bg-[#0B1220] border border-[#1E2E44] rounded-sm p-4 mb-4">
          <div className="text-[#F7FAFC] font-bold text-[13px] mb-2">Structured snippet</div>
          <div className="text-[#9CA8B8] text-[11.5px] mb-2">
            Header: <span className="text-[#F7FAFC] font-bold">{STRUCTURED_SNIPPET.header}</span> (pick this from the dropdown)
          </div>
          <div className="flex items-center justify-between gap-2 bg-[#0F172A] px-3 py-2 rounded-sm">
            <code className="text-[#F7FAFC] text-[12px] flex-1">
              {STRUCTURED_SNIPPET.values.join(", ")}
            </code>
            <CopyBtn
              text={STRUCTURED_SNIPPET.values.join("\n")}
              label="Snippet values"
              testid="ads-launch-copy-snippet"
            />
          </div>
        </div>

        {/* Image extensions */}
        <div className="bg-[#0F172A] border border-[#F7B500]/40 rounded-sm p-4">
          <div className="flex items-start gap-2 mb-2">
            <Image size={14} className="text-[#F7B500] shrink-0 mt-0.5" />
            <div className="text-[#F7FAFC] font-bold text-[13px]">Image extensions — be careful</div>
          </div>
          <p className="text-[#9CA8B8] text-[11.5px] leading-relaxed">
            Upload only your <span className="text-[#5EEAD4]">logo</span>, a stock photo of a{" "}
            <span className="text-[#5EEAD4]">scientist in a lab coat / lab bench</span>, or your site's{" "}
            <span className="text-[#5EEAD4]">cyan-lime hero artwork</span>. <strong>Never</strong> upload product
            vial photos, pills, syringes or anything resembling a medication. Image reviewers are stricter than text reviewers.
          </p>
        </div>
      </Section>

      {/* STEP 7 — After publish */}
      <Section icon={ShieldCheck} kicker="// Step 7" title="What happens after you click Publish">
        <Step
          n="1"
          title="Wait 24-72 hours for review"
          body="Google's bot reviews each ad individually. You'll see 'Eligible' (good) or 'Limited' (mostly good — keep running) or 'Disapproved' (rejected) in the Status column."
        />
        <Step
          n="2"
          title="If an ad is Disapproved"
          body="Click into it to see the specific policy reason. If it says 'Unapproved Pharmaceuticals' or 'Healthcare and Medicines' — DELETE the ad (don't appeal) and create a new one with different wording. Two appeals on the same ad = account-level flag."
        />
        <Step
          n="3"
          title="Let it run for 7 days"
          body="Don't touch anything for the first week. Google's AI needs the data to learn. Resist the urge to pause keywords on day 2."
        />
        <Step
          n="4"
          title="After 7 days"
          body="Come back to me with a screenshot of the campaign overview. I'll tell you what to scale, what to pause and what to add. Conversions are the only metric that matters."
        />
      </Section>

      {/* STEP 8 — Common rejections */}
      <Section icon={Settings} kicker="// Reference" title="If something goes wrong" defaultOpen={false}>
        <table className="w-full text-[12.5px] text-[#9CA8B8]">
          <thead>
            <tr className="border-b border-[#1E2E44]">
              <th className="text-left py-2 pr-3 text-[#F7FAFC] font-semibold">Rejection reason</th>
              <th className="text-left py-2 text-[#F7FAFC] font-semibold">What to change</th>
            </tr>
          </thead>
          <tbody>
            {[
              ["Unapproved Pharmaceuticals and Supplements", "Your headline/description has a molecule name or a verb that triggered it. Remove it. Submit a new ad — don't appeal."],
              ["Healthcare and Medicines", "Add 'research' more aggressively. Change 'peptides' alone to 'research peptides'. Submit a new ad."],
              ["Misleading content", "Usually triggered by superlatives. Avoid 'best', 'cheapest', 'guaranteed'. Stick to factual descriptions."],
              ["Destination not working", "Check the Final URL loads (not 404 / not blank). Check it has a privacy policy + T&Cs link in the footer."],
              ["Suspended account", "STOP. Don't create new ads. Email me a screenshot. We'll appeal once, carefully."],
            ].map(([reason, fix], i) => (
              <tr key={i} className="border-b border-[#1E2E44]/50">
                <td className="py-2.5 pr-3 align-top text-[#F7B500]">{reason}</td>
                <td className="py-2.5 align-top">{fix}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      {/* Final go */}
      <div className="pg-card p-5 text-center border-2 border-[#A6FF00]/40 bg-[#A6FF00]/5">
        <div className="text-[#F7FAFC] text-base font-bold mb-2">All done? You're ready to publish.</div>
        <p className="text-[#9CA8B8] text-[12.5px] mb-4 max-w-2xl mx-auto leading-relaxed">
          Hit "Publish" in Google Ads. Don't change anything for 7 days. The conversion tracking we wired up
          (Purchase, Add to Cart, Begin Checkout, Newsletter Signup, Affiliate Signup) is already feeding signals to Google's AI.
        </p>
        <a
          href="https://ads.google.com/aw/campaigns/new"
          target="_blank"
          rel="noreferrer"
          className="pg-btn-primary"
          data-testid="ads-launch-final-cta"
        >
          Open Google Ads <ArrowRight size={14} />
        </a>
      </div>
    </div>
  );
}
